import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiArrowRight, FiFileText, FiSave, FiCheck } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';
import Step1Identity from '../components/wizard/Step1Identity';
import Step2Configuration from '../components/wizard/Step2Configuration';
import Step3Integrations from '../components/wizard/Step3Integrations';
import Step4FeaturesPages from '../components/wizard/Step4FeaturesPages';
import Step5BusinessSEO from '../components/wizard/Step5BusinessSEO';
import PromptModal from '../components/PromptModal';
import SectionTitle from '../components/SectionTitle';
import { promptCompiler } from '../utils/promptCompiler';

const steps = [
  { id: 1, title: 'Identity', component: Step1Identity },
  { id: 2, title: 'Configuration', component: Step2Configuration },
  { id: 3, title: 'Integrations', component: Step3Integrations },
  { id: 4, title: 'Features & Pages', component: Step4FeaturesPages },
  { id: 5, title: 'Business & SEO', component: Step5BusinessSEO },
];

function StartBuilding() {
  const { projectDraft, dispatch } = useAppContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [projectId, setProjectId] = useState(projectDraft.id || null);

  const CurrentStepComponent = steps[currentStep - 1].component;

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGeneratePrompt = () => {
    try {
      const prompt = promptCompiler(projectDraft);
      setGeneratedPrompt(prompt);
      setIsPromptModalOpen(true);
    } catch (error) {
      console.error('Failed to generate prompt:', error);
    }
  };

  const handleSaveProject = () => {
    // Simulate saving project
    const newId = Date.now().toString();
    setProjectId(newId);
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { id: newId } });
    console.log('Project saved:', projectDraft);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <PromptModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        promptText={generatedPrompt}
        onSave={() => {
          handleSaveProject();
          setIsPromptModalOpen(false);
        }}
      />

      <SectionTitle
        title="Project Wizard"
        subtitle="Build your project step by step with our guided wizard."
      />

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                  currentStep > step.id
                    ? 'bg-green-500 border-green-500 text-white'
                    : currentStep === step.id
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-500'
                }`}
              >
                {currentStep > step.id ? (
                  <FiCheck className="h-5 w-5" />
                ) : (
                  <span className="text-sm font-medium">{step.id}</span>
                )}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700 hidden sm:block">
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className="hidden sm:block w-16 h-0.5 bg-gray-300 mx-4" />
              )}
            </div>
          ))}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="card">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -30, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CurrentStepComponent />
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handlePrev}
                className="btn-secondary"
              >
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Previous
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSaveProject}
              className="btn-secondary"
            >
              <FiSave className="mr-2 h-4 w-4" />
              {projectId ? 'Update Project' : 'Save Draft'}
            </button>
            
            {currentStep === steps.length ? (
              <button
                onClick={handleGeneratePrompt}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105"
              >
                <FiFileText className="mr-2 h-4 w-4" />
                Generate Prompt
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="btn-primary"
              >
                Next
                <FiArrowRight className="ml-2 h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default StartBuilding;